import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Drawer, AppBar, Toolbar, List, Typography, IconButton,
  ListItem, ListItemButton, ListItemIcon, ListItemText, Avatar,
  Divider, useMediaQuery, useTheme, Badge, Menu, MenuItem, Tooltip, alpha
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Payment,
  History,
  AccountBalance,
  Send,
  Logout,
  Close,
  Notifications,
  Person,
  EmojiEvents
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import * as colors from '../config/colorProfile';

const drawerWidth = 260;
const drawerCollapsedWidth = 70;

const Layout = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(true); // ✅ START EXPANDED
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const menuItems = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
    { text: 'Add Collection', icon: <Payment />, path: '/add-collection' },
    { text: 'View History', icon: <History />, path: '/history' },
    { text: 'My Cash Book', icon: <AccountBalance />, path: '/cashbook' },
    { text: 'Cash Management', icon: <Send />, path: '/cash-management' },
    { text: 'Staff Performance', icon: <EmojiEvents />, path: '/staff-performance' }
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const currentDrawerWidth = drawerOpen ? drawerWidth : drawerCollapsedWidth;
  const currentPageName = menuItems.find(item => item.path === location.pathname)?.text || 'VV COLLECT';

  const drawer = (
    <Box 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column', 
        bgcolor: colors.SIDEBAR_BG,
      }}
      onMouseEnter={() => !isMobile && setDrawerOpen(true)}
      onMouseLeave={() => !isMobile && setDrawerOpen(false)}
    >
      {/* ✅ ENHANCED: Sidebar Header */}
      <Box sx={{ 
        p: drawerOpen || isMobile ? 3 : 1.5, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: (drawerOpen || isMobile) ? 'space-between' : 'center',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', // ✅ Smooth easing
        bgcolor: colors.SIDEBAR_BG,
        borderBottom: `1px solid ${colors.SIDEBAR_BORDER}`,
        minHeight: 80 // ✅ Consistent height
      }}>
        {(drawerOpen || isMobile) ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: colors.PRIMARY_COLOR,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 1.5,
                  boxShadow: `0 4px 12px ${alpha(colors.PRIMARY_COLOR, 0.3)}`,
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'scale(1.05)' // ✅ Subtle hover effect
                  }
                }}
              >
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                  V
                </Typography>
              </Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700, 
                  color: colors.SIDEBAR_TEXT,
                  letterSpacing: '0.5px' // ✅ Better readability
                }}
              >
                VV COLLECT
              </Typography>
            </Box>
            {isMobile && (
              <IconButton 
                onClick={handleDrawerToggle} 
                size="small"
                sx={{
                  color: colors.SIDEBAR_TEXT,
                  '&:hover': {
                    bgcolor: colors.SIDEBAR_HOVER,
                    transform: 'rotate(90deg)', // ✅ Animated close
                    transition: 'transform 0.3s'
                  }
                }}
              >
                <Close />
              </IconButton>
            )}
          </>
        ) : (
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: colors.PRIMARY_COLOR,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 4px 12px ${alpha(colors.PRIMARY_COLOR, 0.3)}`,
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'scale(1.1)'
              }
            }}
          >
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
              V
            </Typography>
          </Box>
        )}
      </Box>

      {/* ✅ ENHANCED: User Profile Section */}
      {(drawerOpen || isMobile) && (
        <Box sx={{ 
          p: 3,
          bgcolor: alpha(colors.PRIMARY_COLOR, 0.05),
          borderBottom: `1px solid ${colors.SIDEBAR_BORDER}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                bgcolor: colors.PRIMARY_COLOR,
                color: 'white',
                mr: 2,
                fontWeight: 600,
                boxShadow: `0 4px 12px ${alpha(colors.PRIMARY_COLOR, 0.3)}`,
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'scale(1.05)'
                }
              }}
            >
              {user?.name?.[0] || 'S'}
            </Avatar>
            <Box sx={{ overflow: 'hidden' }}>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontWeight: 600, 
                  color: colors.SIDEBAR_TEXT,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {user?.name || 'Staff Member'}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: colors.TEXT_SECONDARY,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                Collection Agent
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* ✅ ENHANCED: Collapsed Avatar */}
      {!drawerOpen && !isMobile && (
        <Box sx={{ 
          p: 1.5, 
          display: 'flex', 
          justifyContent: 'center',
          borderBottom: `1px solid ${colors.SIDEBAR_BORDER}`
        }}>
          <Tooltip title={user?.name || 'Staff Member'} placement="right" arrow>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: alpha(colors.PRIMARY_COLOR, 0.15),
                color: colors.PRIMARY_COLOR,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: alpha(colors.PRIMARY_COLOR, 0.25),
                  transform: 'scale(1.1)'
                }
              }}
            >
              {user?.name?.[0] || 'S'}
            </Avatar>
          </Tooltip>
        </Box>
      )}

      {/* ✅ ENHANCED: Navigation Menu */}
      <List sx={{ 
        flexGrow: 1, 
        px: (drawerOpen || isMobile) ? 2 : 1, 
        py: 2,
        overflowY: 'auto',
        overflowX: 'hidden',
        // ✅ Custom scrollbar
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          bgcolor: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: alpha(colors.SIDEBAR_TEXT, 0.2),
          borderRadius: '3px',
          '&:hover': {
            bgcolor: alpha(colors.SIDEBAR_TEXT, 0.3),
          }
        }
      }}>
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path;
          
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <Tooltip 
                title={!drawerOpen && !isMobile ? item.text : ''} 
                placement="right" 
                arrow
                enterDelay={300}
              >
                <ListItemButton
                  onClick={() => handleMenuClick(item.path)}
                  sx={{
                    borderRadius: 2,
                    py: 1.5,
                    px: (drawerOpen || isMobile) ? 2 : 1.5,
                    bgcolor: isActive ? alpha(colors.PRIMARY_COLOR, 0.12) : 'transparent',
                    color: isActive ? colors.PRIMARY_COLOR : colors.SIDEBAR_ICON,
                    justifyContent: (drawerOpen || isMobile) ? 'flex-start' : 'center',
                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    // ✅ ENHANCED: Left border indicator
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: isActive ? '4px' : '0px',
                      height: '60%',
                      bgcolor: colors.PRIMARY_COLOR,
                      borderRadius: '0 4px 4px 0',
                      transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
                    },
                    // ✅ ENHANCED: Glow effect on active
                    boxShadow: isActive 
                      ? `0 4px 12px ${alpha(colors.PRIMARY_COLOR, 0.15)}` 
                      : 'none',
                    '&:hover': {
                      bgcolor: isActive 
                        ? alpha(colors.PRIMARY_COLOR, 0.18) 
                        : colors.SIDEBAR_HOVER,
                      color: isActive ? colors.PRIMARY_COLOR : colors.SIDEBAR_TEXT,
                      transform: 'translateX(4px)',
                      boxShadow: isActive 
                        ? `0 6px 16px ${alpha(colors.PRIMARY_COLOR, 0.2)}` 
                        : `0 2px 8px ${alpha(colors.SIDEBAR_TEXT, 0.08)}`,
                      '&::before': {
                        width: '4px'
                      }
                    },
                    // ✅ Active state press effect
                    '&:active': {
                      transform: 'translateX(2px) scale(0.98)'
                    }
                  }}
                >
                  <ListItemIcon sx={{ 
                    color: 'inherit', 
                    minWidth: (drawerOpen || isMobile) ? 40 : 'auto',
                    justifyContent: 'center',
                    transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    transform: isActive ? 'scale(1.1)' : 'scale(1)'
                  }}>
                    {item.icon}
                  </ListItemIcon>
                  {(drawerOpen || isMobile) && (
                    <ListItemText 
                      primary={item.text}
                      primaryTypographyProps={{
                        fontWeight: isActive ? 600 : 500,
                        fontSize: '0.938rem',
                        letterSpacing: isActive ? '0.02em' : 'normal',
                        transition: 'all 0.2s'
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          );
        })}
      </List>

      <Divider sx={{ borderColor: colors.DIVIDER_COLOR }} />

      {/* ✅ ENHANCED: Logout Button */}
      <Box sx={{ p: (drawerOpen || isMobile) ? 2 : 1 }}>
        <Tooltip 
          title={!drawerOpen && !isMobile ? 'Logout' : ''} 
          placement="right" 
          arrow
        >
          <ListItemButton
            onClick={handleLogout}
            sx={{
              borderRadius: 2,
              py: 1.5,
              px: (drawerOpen || isMobile) ? 2 : 1.5,
              color: colors.ERROR_COLOR,
              justifyContent: (drawerOpen || isMobile) ? 'flex-start' : 'center',
              transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                bgcolor: alpha(colors.ERROR_COLOR, 0.12),
                color: colors.ERROR_COLOR,
                transform: 'translateX(4px)',
                boxShadow: `0 4px 12px ${alpha(colors.ERROR_COLOR, 0.15)}`
              },
              '&:active': {
                transform: 'translateX(2px) scale(0.98)'
              }
            }}
          >
            <ListItemIcon sx={{ 
              color: 'inherit', 
              minWidth: (drawerOpen || isMobile) ? 40 : 'auto',
              justifyContent: 'center'
            }}>
              <Logout />
            </ListItemIcon>
            {(drawerOpen || isMobile) && (
              <ListItemText 
                primary="Logout"
                primaryTypographyProps={{
                  fontWeight: 600,
                  fontSize: '0.938rem'
                }}
              />
            )}
          </ListItemButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: colors.PAGE_BG }}>
      {/* ✅ ENHANCED: App Bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { xs: '100%', md: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { md: `${currentDrawerWidth}px` },
          bgcolor: colors.TOPBAR_BG,
          borderBottom: `1px solid ${colors.TOPBAR_BORDER}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', // ✅ Smooth transition
          boxShadow: colors.SHADOW_SM
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            {isMobile && (
              <IconButton
                color="inherit"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ 
                  mr: 2, 
                  color: colors.TOPBAR_ICON,
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: colors.TOPBAR_HOVER,
                    transform: 'rotate(90deg)'
                  }
                }}
              >
                <MenuIcon />
              </IconButton>
            )}

            <Typography 
              variant="h6" 
              sx={{ 
                color: colors.TOPBAR_TEXT,
                fontWeight: 600,
                fontSize: isMobile ? '1rem' : '1.25rem',
                letterSpacing: '0.5px'
              }}
            >
              {currentPageName}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton 
              sx={{ 
                color: colors.TOPBAR_ICON,
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: colors.TOPBAR_HOVER,
                  transform: 'scale(1.05)'
                }
              }}
            >
              <Badge 
                badgeContent={3} 
                color="error"
                sx={{
                  '& .MuiBadge-badge': {
                    animation: 'pulse 2s infinite'
                  },
                  '@keyframes pulse': {
                    '0%, 100%': {
                      transform: 'scale(1)',
                      opacity: 1
                    },
                    '50%': {
                      transform: 'scale(1.1)',
                      opacity: 0.8
                    }
                  }
                }}
              >
                <Notifications />
              </Badge>
            </IconButton>
            <IconButton 
              onClick={handleProfileMenuOpen} 
              sx={{ 
                color: colors.TOPBAR_ICON,
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: colors.TOPBAR_HOVER,
                  transform: 'scale(1.05)'
                }
              }}
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: colors.PRIMARY_COLOR,
                  color: 'white',
                  fontWeight: 600,
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'scale(1.1)'
                  }
                }}
              >
                {user?.name?.[0] || 'S'}
              </Avatar>
            </IconButton>
          </Box>

          {/* ✅ ENHANCED: Profile Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              sx: {
                mt: 1.5,
                minWidth: 220,
                borderRadius: 2,
                boxShadow: colors.SHADOW_LG,
                border: `1px solid ${colors.BORDER_COLOR}`,
                overflow: 'visible',
                '&::before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                  borderLeft: `1px solid ${colors.BORDER_COLOR}`,
                  borderTop: `1px solid ${colors.BORDER_COLOR}`,
                }
              }
            }}
            TransitionProps={{
              timeout: 200
            }}
          >
            <MenuItem 
              disabled
              sx={{
                '&.Mui-disabled': {
                  opacity: 1,
                  bgcolor: alpha(colors.PRIMARY_COLOR, 0.05)
                }
              }}
            >
              <Person sx={{ mr: 1.5, color: colors.PRIMARY_COLOR }} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: colors.TEXT_PRIMARY }}>
                  {user?.name || 'Staff Member'}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.TEXT_SECONDARY }}>
                  Collection Agent
                </Typography>
              </Box>
            </MenuItem>
            <Divider sx={{ borderColor: colors.DIVIDER_COLOR }} />
            <MenuItem 
              onClick={handleLogout}
              sx={{
                color: colors.ERROR_COLOR,
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: colors.ERROR_BG,
                  transform: 'translateX(4px)'
                }
              }}
            >
              <Logout sx={{ mr: 1.5, fontSize: 20 }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Logout
              </Typography>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Sidebar Drawer */}
      <Box
        component="nav"
        sx={{ 
          width: { xs: 0, md: currentDrawerWidth }, 
          flexShrink: { md: 0 } 
        }}
      >
        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: 'none'
            }
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: currentDrawerWidth,
              borderRight: `1px solid ${colors.SIDEBAR_BORDER}`,
              transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)', // ✅ Smooth expansion
              overflowX: 'hidden'
            }
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { xs: '100%', md: `calc(100% - ${currentDrawerWidth}px)` },
          minHeight: '100vh',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', // ✅ Smooth resize
          bgcolor: colors.PAGE_BG
        }}
      >
        <Toolbar />
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
